import * as Sentry from "@sentry/react";

/**
 * Utility functions for Sentry error tracking and performance monitoring
 */

/**
 * Capture an exception with additional context
 * @param {Error} error - The error to capture
 * @param {Object} context - Additional context to include
 */
export const captureException = (error, context = {}) => {
  Sentry.captureException(error, {
    ...context,
    tags: {
      ...context.tags,
      source: "manual",
    },
  });
};

/**
 * Capture a message with level and context
 * @param {string} message - The message to capture
 * @param {string} level - The log level (error, warning, info, debug)
 * @param {Object} context - Additional context to include
 */
export const captureMessage = (message, level = "info", context = {}) => {
  Sentry.captureMessage(message, level, context);
};

/**
 * Create a span for performance monitoring
 * @param {Object} spanOptions - Options for the span
 * @param {Function} callback - Function to execute within the span
 * @returns {Promise|any} - Result of the callback function
 */
export const createSpan = (spanOptions, callback) => {
  return Sentry.startSpan(spanOptions, callback);
};

/**
 * Add breadcrumb for debugging
 * @param {string} message - The breadcrumb message
 * @param {string} category - The breadcrumb category
 * @param {Object} data - Additional data
 */
export const addBreadcrumb = (message, category = "custom", data = {}) => {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: "info",
  });
};

/**
 * Set user context for error tracking
 * @param {Object} user - User information
 */
export const setUser = (user) => {
  Sentry.setUser(user);
};

/**
 * Set additional context for error tracking
 * @param {string} key - Context key
 * @param {Object} context - Context data
 */
export const setContext = (key, context) => {
  Sentry.setContext(key, context);
};

/**
 * Set tags for error tracking
 * @param {Object} tags - Tags to set
 */
export const setTags = (tags) => {
  Sentry.setTags(tags);
};

/**
 * Get the Sentry logger for structured logging
 * @returns {Object} Sentry logger
 */
export const getLogger = () => {
  return Sentry;
};

/**
 * Common span operations for UI interactions
 */
export const uiSpans = {
  /**
   * Create a span for button clicks
   * @param {string} buttonName - Name of the button
   * @param {Function} callback - Function to execute
   */
  buttonClick: (buttonName, callback) => {
    return createSpan(
      {
        op: "ui.click",
        name: `Button Click: ${buttonName}`,
      },
      callback
    );
  },

  /**
   * Create a span for form submissions
   * @param {string} formName - Name of the form
   * @param {Function} callback - Function to execute
   */
  formSubmit: (formName, callback) => {
    return createSpan(
      {
        op: "ui.form.submit",
        name: `Form Submit: ${formName}`,
      },
      callback
    );
  },

  /**
   * Create a span for navigation
   * @param {string} route - Route being navigated to
   * @param {Function} callback - Function to execute
   */
  navigation: (route, callback) => {
    return createSpan(
      {
        op: "navigation",
        name: `Navigate to: ${route}`,
      },
      callback
    );
  },
};

/**
 * Common span operations for API calls
 */
export const apiSpans = {
  /**
   * Create a span for HTTP requests
   * @param {string} method - HTTP method
   * @param {string} url - Request URL
   * @param {Function} callback - Function to execute
   */
  httpRequest: (method, url, callback) => {
    return createSpan(
      {
        op: "http.client",
        name: `${method.toUpperCase()} ${url}`,
      },
      callback
    );
  },

  /**
   * Create a span for database operations
   * @param {string} operation - Database operation
   * @param {string} table - Database table
   * @param {Function} callback - Function to execute
   */
  database: (operation, table, callback) => {
    return createSpan(
      {
        op: "db",
        name: `${operation} ${table}`,
      },
      callback
    );
  },
};
